import { useEffect, useRef, useState } from "react";
import { useParams, Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Megaphone, ArrowLeft, MapPin, MessageCircle, Heart, Lock, ArrowUpRight, Users } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { CATEGORIES, CITIES } from "@/lib/categories";
import { formatDate, getUser } from "@/lib/user";
import { LovePreferencesGate } from "@/components/LovePreferencesGate";
import { QuartierFilter } from "@/components/QuartierFilter";
import { ContactAnnonceur } from "@/components/ContactAnnonceur";
import { PartageAnnonce } from "@/components/PartageAnnonce";
import { BadgesMedias } from "@/components/BadgesMedias";
import { lienAnnonce, estNouvelleAnnonce, BadgeNouveau } from "@/lib/annonces";
import { PastilleEnLigne } from "@/components/PastilleEnLigne";
import { BandeauApercu } from "@/components/BandeauApercu";
import { useApercu, versionMedia } from "@/lib/apercu";
import { RetourVuePrecedente } from "@/lib/retourVue";
import { ReactionsLove } from "@/components/ReactionsLove";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const mediaUrl = (u) => {
  if (!u.startsWith("/api")) return u;
  const user = getUser();
  return `${process.env.REACT_APP_BACKEND_URL}${u}?v=${versionMedia()}${user ? `&viewer_id=${user.id}` : ""}`;
};
const PALIER_LABELS = { gratuit: "Gratuit", semaine: "Semaine", mois: "Mois" };
const DATES = [
  { value: "", label: "Toutes dates" },
  { value: "1", label: "Dernières 24h" },
  { value: "7", label: "7 derniers jours" },
  { value: "30", label: "30 derniers jours" },
];

const AnnonceCard = ({ annonce, cat, index, floute, estLove, moiId }) => {
  const [ouverte, setOuverte] = useState(false);
  const [coeurs, setCoeurs] = useState(0);
  const navigate = useNavigate();

  const likerAnnonce = async () => {
    const user = getUser();
    if (!user) {
      toast.error("Créez votre profil pour envoyer un cœur.");
      navigate("/inscription");
      return;
    }
    try {
      const res = await axios.post(`${API}/fodindinlove/annonces/${annonce.id}/liker`, { visiteur_id: user.id });
      setCoeurs((c) => c + 1);
      toast.success("Cœur envoyé !", { description: `Total : ${res.data.nombreLikesTotal} cœur(s) sur cette annonce.` });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const contacter = async () => {
    const user = getUser();
    if (!user) {
      toast.error("Créez votre profil pour contacter l'annonceur.");
      navigate("/inscription");
      return;
    }
    if (user.id === annonce.annonceur?.id) {
      toast.error("C'est votre propre annonce !");
      return;
    }
    try {
      const conv = await axios.post(`${API}/conversations`, { from_id: user.id, to_id: annonce.annonceur.id });
      toast.success(`Conversation ouverte avec ${annonce.annonceur.firstname}`);
      navigate(`/messages?conv=${conv.data.id}`);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error("La messagerie est réservée aux abonnés.");
        navigate("/abonnement");
      } else {
        toast.error(err.response?.data?.detail || "Erreur");
      }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, delay: (index % 4) * 0.08 }}
      className="bg-white rounded-2xl border border-[#E5DFD5] overflow-hidden hover:shadow-xl transition-shadow duration-300"
      data-testid={`annonce-publique-${annonce.id}`}
    >
      <button className="relative w-full h-56 block" onClick={() => setOuverte(!ouverte)} data-testid={`annonce-publique-toggle-${annonce.id}`}>
        {annonce.photos[0] && (
          <img src={mediaUrl(annonce.photos[0].url)} alt="Annonce"
               data-testid={`annonce-photo-${annonce.id}`}
               className={`w-full h-full object-cover object-top transition-[filter] duration-500 ${
                 floute ? "blur-[14px] scale-110" : ""}`} />
        )}
        <BadgesMedias
          nbPhotos={annonce.nb_photos || annonce.photos?.length || 0}
          aVideo={annonce.a_video}
          testid={`annonce-medias-${annonce.id}`}
        />
        {floute && (
          <span className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-1.5 bg-black/25"
                data-testid={`annonce-photo-verrou-${annonce.id}`}>            <Lock size={18} className="text-white" />
            <span className="text-[11px] font-bold uppercase tracking-wider text-white">Abonnez-vous pour voir</span>
          </span>
        )}
        <span className="absolute bottom-3 right-3 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full text-white" style={{ backgroundColor: cat.color }}>
          {PALIER_LABELS[annonce.palier]}
        </span>
      </button>
      <div className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-heading flex items-center gap-2 text-sm font-bold text-stone-900">
              {annonce.annonceur?.firstname}
              <PastilleEnLigne enLigne={annonce.annonceur?.en_ligne}
                               testid={`presence-${annonce.id}`} />
            </div>
            <div className="flex items-center gap-1 text-xs text-stone-500 mt-0.5">
              <MapPin size={11} /> {annonce.annonceur?.city} · {formatDate(annonce.date_debut)}
            </div>
          </div>
        </div>
        {annonce.texte && (
          <p className="text-sm text-stone-600 leading-relaxed line-clamp-3" data-testid={`annonce-texte-${annonce.id}`}>
            {annonce.texte}
          </p>
        )}
        {/* Le texte complet (jusqu'à 1000 caractères) s'ouvre dans sa page dédiée. */}
        <a
          href={lienAnnonce(annonce)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`annonce-lire-complete-${annonce.id}`}
          className="inline-flex w-full items-center justify-center gap-2 rounded-full py-2.5 text-sm font-bold text-white transition-transform hover:scale-[1.02]"
          style={{ backgroundColor: cat.color }}
        >
          Lire l'annonce complète <ArrowUpRight size={15} />
        </a>
        {/* FodindinLove : les 4 réactions sont visibles directement sur l'annonce. */}
        {(estLove || annonce.categorie === "love") && (
          <ReactionsLove cibleId={annonce.annonceur?.id || annonce.profile_id}
            annonceId={annonce.id} moiId={moiId} reactionInitiale={annonce.ma_reaction}
            onMatch={() => toast.success("C'est un match ! Ouvrez FodindinLove pour discuter.")} />
        )}
        <div className="flex gap-2">
          {(estLove || annonce.categorie === "love") && (
            <button
              onClick={likerAnnonce}
              data-testid={`liker-annonce-${annonce.id}`}
              title="Envoyer un cœur"
              className={`inline-flex items-center justify-center gap-1.5 text-sm font-semibold px-4 py-2.5 rounded-full border-2 transition-all duration-200 ${
                coeurs > 0 ? "border-[#9f2558] bg-[#9f2558]/10 text-[#9f2558]" : "border-[#E5DFD5] text-stone-500 hover:border-[#9f2558] hover:text-[#9f2558]"
              }`}
            >
              <Heart size={15} fill={coeurs > 0 ? "#9f2558" : "none"} />
            </button>
          )}
          <button
            onClick={contacter}
            data-testid={`contacter-annonceur-${annonce.id}`}
            className="flex-1 inline-flex items-center justify-center gap-2 text-sm font-semibold py-2.5 rounded-full border-2 transition-all duration-200 hover:text-white"
            style={{ borderColor: cat.color, color: cat.color }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = cat.color)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
          >
            <MessageCircle size={15} /> Contacter l'annonceur
          </button>
        </div>
        <ContactAnnonceur annonceId={annonce.id} couleur={cat.color} />
        {(estLove || annonce.categorie === "love") && (
          <Link to="/love?onglet=admirateurs" data-testid={`annonce-qui-ma-aime-${annonce.id}`}
            className="inline-flex w-full items-center justify-center gap-2 rounded-full border-2 border-[#9f2558]/30 py-2.5 text-sm font-semibold text-[#9f2558] transition-colors hover:bg-[#9f2558]/10">
            <Users size={15} /> Qui m'a aimé
          </Link>
        )}
        <div className="mt-3 flex justify-end">
          <PartageAnnonce annonceId={annonce.id} reference={annonce.slug}
            titre={`Annonce de ${annonce.annonceur?.firstname}`} />
        </div>
        {ouverte && (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              {annonce.photos.map((p, i) => (
                <img key={i} src={mediaUrl(p.url)} alt=""
                     className={`w-full h-20 object-cover rounded-lg border border-[#E5DFD5] ${
                       floute ? "blur-[8px]" : ""}`} />
              ))}
            </div>
            {annonce.video && <video src={mediaUrl(annonce.video.url)} controls className="w-full rounded-lg" />}
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default function ConsulterAnnonces() {
  const { categoryId } = useParams();
  const cat = CATEGORIES[categoryId];
  const moi = getUser();
  const [annonces, setAnnonces] = useState([]);
  const [ville, setVille] = useState("Tous");
  const [commune, setCommune] = useState("");
  const [quartier, setQuartier] = useState("");
  const [depuis, setDepuis] = useState("");
  const [page, setPage] = useState(1);
  const [finListe, setFinListe] = useState(false);
  const [loading, setLoading] = useState(true);
  const requeteEnCours = useRef(0);
  const apercu = useApercu();
  const floute = apercu.connecte && !apercu.photosNettes(categoryId);
  const [params] = useSearchParams();
  const acquitte = apercu.acces?.[categoryId];

  // Retour de paiement : on attend l'activation (webhook CinetPay) sans laisser le membre perdu.
  useEffect(() => {
    if (params.get("paye") !== "1") return;
    if (acquitte) {
      toast.success("Abonnement actif", { description: `Bienvenue dans ${cat?.name} — photos et numéros débloqués.` });
      return;
    }
    const attente = setTimeout(apercu.recharger, 3000);
    return () => clearTimeout(attente);
  }, [params, acquitte]);
  const [lovePrefs, setLovePrefs] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem("fodindinLove_etape1"));
    } catch {
      return null;
    }
  });

  useEffect(() => {
    setCommune("");
    setQuartier("");
  }, [ville]);

  useEffect(() => {
    setPage(1);
  }, [ville, commune, quartier, depuis, categoryId, lovePrefs]);

  useEffect(() => {
    if (!cat) return;
    if (categoryId === "love" && !lovePrefs) return;
    setLoading(true);
    const params = { categorie: categoryId, page, viewer_id: moi?.id || undefined };
    if (ville !== "Tous") params.ville = ville;
    if (commune) params.commune = commune;
    if (quartier) params.quartier = quartier;
    if (depuis) params.depuis_jours = depuis;
    if (categoryId === "love" && lovePrefs) {
      const map = { homme: "Homme", femme: "Femme" };
      if (map[lovePrefs.genreRecherche]) params.genre = map[lovePrefs.genreRecherche];
    }
    const jeton = ++requeteEnCours.current;
    axios
      .get(`${API}/annonces/publiques`, { params })
      .then((r) => {
        // Réponse obsolète (filtre changé entre-temps) : on l'ignore, sinon la liste
        // affichée est remplacée par des annonces qui ne correspondent plus.
        if (jeton !== requeteEnCours.current) return;
        setAnnonces((prev) => (page === 1 ? r.data : [...prev, ...r.data]));
        setFinListe(r.data.length < 20);
      })
      .catch(() => { if (jeton === requeteEnCours.current) setFinListe(true); })
      .finally(() => { if (jeton === requeteEnCours.current) setLoading(false); });
  }, [categoryId, page, ville, commune, quartier, depuis, cat, lovePrefs, moi?.id]);

  if (!cat) return <Navigate to="/" replace />;

  if (categoryId === "love" && !lovePrefs) {
    return <LovePreferencesGate onComplete={setLovePrefs} />;
  }

  return (
    <div className="bg-[#FAF8F5] min-h-screen">
      <Navbar />
      <header className="relative bg-[#12100E] pt-32 pb-16 overflow-hidden">
        <div className="absolute -top-24 -right-24 w-[380px] h-[380px] rounded-full blur-[130px]" style={{ background: cat.color, opacity: 0.35 }} />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6 flex flex-wrap items-center gap-3">
            <Link to={`/categorie/${categoryId}/choix`} data-testid="consulter-retour" className="inline-flex items-center gap-2 text-sm text-stone-400 hover:text-white transition-colors">
              <ArrowLeft size={15} /> Retour
            </Link>
            {params.get("paye") === "1" && (
              <RetourVuePrecedente libelle="Revenir à l'annonce que je consultais" />
            )}
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-stone-50">
            Annonces <span style={{ color: cat.color }}>{cat.name}</span>
          </h1>
          <p className="text-stone-400 mt-4 max-w-xl leading-relaxed text-sm">
            Uniquement les annonces actives de cette catégorie — la séparation entre catégories est stricte.
          </p>
          {/* Parcours clair après un paiement : le membre voit où publier son annonce. */}
          <Link to={`/annonces?cat=${categoryId}`} data-testid="consulter-deposer-annonce"
            className="mt-5 inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-bold text-white transition-transform hover:scale-[1.02]"
            style={{ backgroundColor: cat.color }}>
            <Megaphone size={15} /> Déposer mon annonce dans {cat.name}
          </Link>
          {categoryId === "love" && lovePrefs && (
            <button
              onClick={() => {
                sessionStorage.removeItem("fodindinLove_etape1");
                setLovePrefs(null);
              }}
              data-testid="modifier-choix-love"
              className="mt-4 text-sm font-semibold underline underline-offset-4 hover:opacity-80 transition-opacity"
              style={{ color: cat.color }}
            >
              Je recherche : {lovePrefs.genreRecherche === "peu_importe" ? "peu importe" : `un${lovePrefs.genreRecherche === "femme" ? "e femme" : " homme"}`} — Modifier mes choix
            </button>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        {/* filtres */}
        <div className="flex flex-wrap items-center gap-2 mb-10" data-testid="annonces-filtres">
          {CITIES.map((c) => (
            <button
              key={c}
              onClick={() => setVille(c)}
              data-testid={`annonces-filtre-ville-${c.toLowerCase().replace("é", "e")}`}
              className={`px-4 py-2 rounded-full text-sm font-medium border transition-all duration-200 ${
                ville === c ? "text-white border-transparent" : "bg-white text-stone-600 border-[#E5DFD5] hover:border-stone-400"
              }`}
              style={ville === c ? { backgroundColor: cat.color } : {}}
            >
              {c}
            </button>
          ))}
          <QuartierFilter ville={ville} commune={commune} setCommune={setCommune} quartier={quartier} setQuartier={setQuartier} />
          <select
            value={depuis}
            onChange={(e) => setDepuis(e.target.value)}
            data-testid="annonces-filtre-date"
            className="ml-auto bg-white border border-[#E5DFD5] rounded-full px-4 py-2 text-sm font-medium text-stone-600 focus:outline-none focus:ring-2 focus:ring-stone-300"
          >
            {DATES.map((d) => (
              <option key={d.value} value={d.value}>{d.label}</option>
            ))}
          </select>
        </div>

        <BandeauApercu apercu={apercu} categorie={categoryId} />

        {loading && page === 1 ? (
          <div className="flex justify-center py-24">
            <Loader2 className="animate-spin" size={36} style={{ color: cat.color }} />
          </div>
        ) : annonces.length === 0 ? (
          <div className="text-center py-24" data-testid="annonces-vide">
            <Megaphone size={40} className="mx-auto mb-5 text-stone-300" />
            <p className="text-stone-500">Aucune annonce ne correspond à ces critères.</p>
            <Link to={`/annonces?cat=${categoryId}`} data-testid="annonces-vide-cta" className="mt-6 inline-block font-semibold" style={{ color: cat.color }}>
              Soyez le premier à publier →
            </Link>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="annonces-publiques-grid">
              {annonces.map((a, i) => (
                <AnnonceCard key={a.id} annonce={a} cat={cat} index={i} floute={floute}
                  estLove={categoryId === "love"} moiId={moi?.id} />
              ))}
            </div>
            {!finListe && (
              <div className="mt-12 text-center">
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={loading}
                  data-testid="annonces-charger-plus"
                  className="inline-flex items-center gap-2 font-semibold px-8 py-3.5 rounded-full border-2 transition-colors"
                  style={{ borderColor: cat.color, color: cat.color }}
                >
                  {loading && <Loader2 size={15} className="animate-spin" />} Charger plus
                </button>
              </div>
            )}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
